import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { homeActions } from 'app/pages/Home/slice';
import { monthlyTimelineActions } from 'app/components/MonthlyTimeline/store/monthlyTimelineSlice';
import { ToastQueue } from '@adobe/react-spectrum';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCurrentViewState } from 'app/hooks/useCurrentViewState';
import {
  selectActiveSavedViewId,
  selectAudiences,
  selectCategoryProductFilter,
  selectCategoryToProductMap,
  selectCompact,
  selectDateRange,
  selectFeatures,
  selectGenAiFilter,
  selectLanesTiers,
  selectMomentTypeFilter,
  selectMustWinView,
  selectMyStuffView,
  selectParkingLot,
  selectProductFilter,
  selectRevenueImpactFilter,
  selectSavedViewApplyToken,
  selectStatus
} from 'app/pages/Home/slice/selectors';
import { selectSavedViews } from 'app/components/SavedViews/slice/selectors';
import { applySavedViewState } from 'app/utils/applyViewStateFromNavigation';
import { stashPendingRoadmapIssueIds } from 'app/utils/pendingRoadmapIssueIds';
import type { SavedView } from 'app/services/savedViewsApi';
import {
  parseViewStateFromSearchParams,
  generateViewStateDeepLink,
  type ViewStateParams
} from 'utils/viewStateDeepLink';
import {
  clearSharedViewSession,
  appendSharedViewFlagToUrl,
  isSharedViewMode,
  markSharedViewUrlStateApplied,
  markSavedViewActive,
  syncSharedViewSessionFromSearch
} from 'utils/sharedViewMode';
import { getInitialDateRange } from 'utils/helpers';
import { clearSavedViewSnapshot } from 'app/utils/persistSavedViewPreferences';
import { MY_STUFF_VIEW_ID } from 'app/components/SavedViews/constants';

const OPENING_VIEW_CLEAR_DELAY_MS = 1200;

// Module-level baseline so it survives monthly↔quarterly remount (same session, one user).
let savedViewFilterBaseline: string | null = null;

// Stash of the user's shared Roadmap/My Stuff preferences, captured the
// moment they leave that shared context for a real Saved View, and restored
// (instead of resetting to a hard default) when they come back — via
// Roadmap/Milestones/Must Wins nav, or by opening My Stuff from within a
// Saved View. Cleared once restored, so the next Saved View entry captures
// a fresh snapshot reflecting whatever was last true in the shared context.
let sharedPreferencesSnapshot: ViewStateParams | null = null;

// Module-level (not component-instance) grace-period flag, with its own
// timer independent of any component's lifecycle. Applying a Saved View
// dispatches a burst of filter changes; some of those land in OTHER
// components with their own "helpful default" effects (e.g. NewFilter
// auto-selecting default tiers when it sees an empty selection), which can
// re-fire on mount and legitimately-but-wrongly treat the Saved View's own
// intentional empty/default fields as "nothing set yet." When the Saved
// View's target is a different top-level page (e.g. Milestones), this
// whole component tree remounts, so a component-state grace flag (like
// openingSavedViewId, which still exists for the per-row "opening" visual
// indicator) resets to null on the new instance right when it's needed
// most. This flag survives that remount, giving the divergence-check effect
// below the same grace window on cross-page transitions that it already had
// on same-page ones.
let isApplyingSavedView = false;
let applyingSavedViewTimeoutId: ReturnType<typeof setTimeout> | null = null;

function markApplyingSavedView() {
  isApplyingSavedView = true;
  if (applyingSavedViewTimeoutId) {
    clearTimeout(applyingSavedViewTimeoutId);
  }
  applyingSavedViewTimeoutId = setTimeout(() => {
    isApplyingSavedView = false;
    applyingSavedViewTimeoutId = null;
  }, OPENING_VIEW_CLEAR_DELAY_MS);
}

export function useSavedViewActions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const categoryToProductMap = useSelector(selectCategoryToProductMap);
  const getCurrentViewState = useCurrentViewState();
  const [openingSavedViewId, setOpeningSavedViewId] = useState<string | null>(
    null
  );
  const openingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Active saved view highlight — stored in Redux so it survives monthly↔quarterly remount
  const activeSavedViewId = useSelector(selectActiveSavedViewId);
  const setActiveSavedViewId = useCallback(
    (id: string | null) => dispatch(homeActions.setActiveSavedViewId(id)),
    [dispatch]
  );
  const savedViewApplyToken = useSelector(selectSavedViewApplyToken);
  const prevTokenRef = useRef(savedViewApplyToken);

  // True only for a real, user-created Saved View — not My Stuff, not "none active".
  const isRealSavedViewActive =
    !!activeSavedViewId && activeSavedViewId !== MY_STUFF_VIEW_ID;

  // Let ordinary filter-change persistence (saveUserPreferences, ~150 call
  // sites app-wide) know when a real Saved View is active, so it stops
  // writing into the shared Roadmap/My Stuff preferences blob while the
  // user is viewing/tweaking a Saved View's own filters.
  useEffect(() => {
    markSavedViewActive(isRealSavedViewActive);
  }, [isRealSavedViewActive]);

  // Filter selectors — used to detect when user changes filters after opening a view
  const audiences = useSelector(selectAudiences);
  const categoryProductFilter = useSelector(selectCategoryProductFilter);
  const mustWinViewActive = useSelector(selectMustWinView);
  const productFilter = useSelector(selectProductFilter);
  const dateRange = useSelector(selectDateRange);
  const genAiFilter = useSelector(selectGenAiFilter);
  const revenueImpactFilter = useSelector(selectRevenueImpactFilter);
  const momentTypeFilter = useSelector(selectMomentTypeFilter);
  const status = useSelector(selectStatus);
  const features = useSelector(selectFeatures);
  const lanesTiers = useSelector(selectLanesTiers);
  const isCompact = useSelector(selectCompact);
  const parkingLot = useSelector(selectParkingLot);
  const savedViews = useSelector(selectSavedViews);

  const filterFingerprint = useMemo(
    () =>
      JSON.stringify({
        sa: audiences.selectedAudiences,
        sc: categoryProductFilter.selectedCategories,
        sp: categoryProductFilter.selectedProducts,
        spr: productFilter.selectedProducts,
        mw: mustWinViewActive,
        dr: dateRange,
        ga: genAiFilter.selectedFilters,
        ri: revenueImpactFilter.selectedFilters,
        mt: momentTypeFilter.selectedMomentTypes,
        ss: status.selectedStatus,
        sf: features.selectedFeatures,
        lt: lanesTiers.selectedLanesTiers,
        ic: isCompact,
        pl: parkingLot
      }),
    [
      audiences.selectedAudiences,
      categoryProductFilter,
      productFilter.selectedProducts,
      mustWinViewActive,
      dateRange,
      genAiFilter.selectedFilters,
      revenueImpactFilter.selectedFilters,
      momentTypeFilter.selectedMomentTypes,
      status.selectedStatus,
      features.selectedFeatures,
      lanesTiers.selectedLanesTiers,
      isCompact,
      parkingLot
    ]
  );

  // Stable ref so the mount-only effect below can read the initial fingerprint
  // without it being a dependency (which would defeat the mount-only intent).
  const filterFingerprintRef = useRef(filterFingerprint);
  filterFingerprintRef.current = filterFingerprint;

  // Single place that clears the active-saved-view highlight, so every exit path
  // (filters diverging, the view being deleted, or navigating to Roadmap/Milestones
  // via LeftSidebar) resets both the Redux flag and the module-level baseline
  // together instead of duplicating the pair of statements at each call site.
  const clearActiveSavedView = useCallback(() => {
    setActiveSavedViewId(null);
    savedViewFilterBaseline = null;
    clearSavedViewSnapshot();
  }, [setActiveSavedViewId]);

  // Capture the user's current shared Roadmap/My Stuff preferences, but only
  // if we're not already coming from another real Saved View (switching
  // Saved-View-to-Saved-View must keep the ORIGINAL pre-Saved-View snapshot,
  // not overwrite it with the first Saved View's own state).
  const stashSharedPreferencesIfNeeded = useCallback(() => {
    if (!isRealSavedViewActive) {
      sharedPreferencesSnapshot = getCurrentViewState();
    }
  }, [isRealSavedViewActive, getCurrentViewState]);

  // Re-apply the stashed shared preferences — reuses replaceViewStateFromSavedView
  // (the same reducer used to apply a Saved View) so filters/tiers/date range are
  // cleared-then-applied atomically, plus the separate timeline-mode slice, then
  // refetches (unless the caller is about to trigger its own fetch right after,
  // e.g. handleOpenMyStuff). Consumes the stash so the next Saved View entry
  // captures fresh. Returns false if there was nothing stashed.
  const restoreSharedPreferences = useCallback(
    (options?: { skipFetch?: boolean }) => {
      const snapshot = sharedPreferencesSnapshot;
      if (!snapshot) return false;

      dispatch(homeActions.replaceViewStateFromSavedView(snapshot));
      if (snapshot.timelineViewMode) {
        dispatch(
          monthlyTimelineActions.setTimelineViewMode(snapshot.timelineViewMode)
        );
      }
      if (snapshot.timelineIconsVisible !== undefined) {
        dispatch(
          monthlyTimelineActions.setTimelineIconsVisible(
            snapshot.timelineIconsVisible
          )
        );
      }
      if (!options?.skipFetch) {
        dispatch(homeActions.loadReleaseData({}));
      }
      sharedPreferencesSnapshot = null;
      return true;
    },
    [dispatch]
  );

  // Explicit "leave the active Saved View and go back to shared Roadmap/My
  // Stuff preferences" transition — used when the user navigates to Roadmap
  // or Milestones. Only a REAL Saved View needs its filters/date-range/
  // toggles restored; leaving My Stuff needs nothing restored at all — My
  // Stuff already shares Roadmap's live state, so there's no snapshot to
  // apply, just the data-source flag to flip back off. Treating My Stuff the
  // same as a real Saved View here previously caused Roadmap<->My Stuff
  // filter edits to be discarded (falling through to the hard-default reset
  // whenever nothing had been stashed yet).
  const exitActiveSavedView = useCallback(() => {
    if (!activeSavedViewId) return;

    if (isRealSavedViewActive) {
      if (!restoreSharedPreferences()) {
        // Nothing stashed (e.g. arrived via a direct Saved View deep link,
        // with no prior shared-context session state) — fall back to default.
        const defaultRange = getInitialDateRange();
        dispatch(
          homeActions.replaceViewStateFromSavedView({
            startDate: defaultRange.start,
            endDate: defaultRange.end
          })
        );
        dispatch(homeActions.loadReleaseData({}));
      }
    } else {
      dispatch(homeActions.setMyStuffView(false));
      dispatch(homeActions.loadReleaseData({}));
    }

    clearActiveSavedView();
  }, [
    activeSavedViewId,
    isRealSavedViewActive,
    dispatch,
    clearActiveSavedView,
    restoreSharedPreferences
  ]);

  // On remount after a monthly↔quarterly switch, prevTokenRef resets to the already-bumped
  // token so the token effect below will not fire. If there is already an active view but no
  // baseline, capture the current fingerprint immediately — by remount time all Redux filter
  // updates from applySavedViewState are committed, so this IS the correct post-apply baseline.
  useEffect(() => {
    if (activeSavedViewId && savedViewFilterBaseline === null) {
      savedViewFilterBaseline = filterFingerprintRef.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only

  // When bumpSavedViewApplyToken fires (after applySavedViewState sets all filters),
  // capture the current filter fingerprint as the baseline for this saved view.
  useEffect(() => {
    if (savedViewApplyToken !== prevTokenRef.current) {
      prevTokenRef.current = savedViewApplyToken;
      savedViewFilterBaseline = filterFingerprint;
    }
  }, [savedViewApplyToken, filterFingerprint]);

  // Clear the active highlight as soon as filters diverge from the saved-view baseline.
  // Skip while a view is being opened — filter changes and the token bump land in the same
  // React batch, so the clear effect would otherwise fire before the token effect can
  // update the baseline to the new view's fingerprint. isApplyingSavedView extends this
  // same grace window across a cross-page remount (openingSavedViewId alone does not —
  // see its module-level counterpart above).
  useEffect(() => {
    // My Stuff is a data-source toggle, not a filter snapshot — it must stay
    // active while the user layers additional filters on top of it.
    if (activeSavedViewId === MY_STUFF_VIEW_ID) return;
    if (!activeSavedViewId || savedViewFilterBaseline === null) return;
    if (openingSavedViewId || isApplyingSavedView) return;
    if (filterFingerprint !== savedViewFilterBaseline) {
      clearActiveSavedView();
    }
  }, [
    filterFingerprint,
    activeSavedViewId,
    openingSavedViewId,
    clearActiveSavedView
  ]);

  // Clear when the active view is deleted.
  useEffect(() => {
    // My Stuff is never part of the API-backed savedViews list, so skip this check.
    if (activeSavedViewId === MY_STUFF_VIEW_ID) return;
    if (!activeSavedViewId) return;
    if (!savedViews.some(v => v.id === activeSavedViewId)) {
      clearActiveSavedView();
    }
  }, [savedViews, activeSavedViewId, clearActiveSavedView]);

  useEffect(() => {
    return () => {
      if (openingTimeoutRef.current) {
        clearTimeout(openingTimeoutRef.current);
      }
    };
  }, []);

  const clearOpeningSavedView = useCallback(() => {
    if (openingTimeoutRef.current) {
      clearTimeout(openingTimeoutRef.current);
      openingTimeoutRef.current = null;
    }
    setOpeningSavedViewId(null);
  }, []);

  const scheduleClearOpeningSavedView = useCallback(() => {
    if (openingTimeoutRef.current) {
      clearTimeout(openingTimeoutRef.current);
    }
    openingTimeoutRef.current = setTimeout(() => {
      setOpeningSavedViewId(null);
      openingTimeoutRef.current = null;
    }, OPENING_VIEW_CLEAR_DELAY_MS);
  }, []);

  const handleCopyLink = useCallback(async (savedView: SavedView) => {
    try {
      const absoluteLink = new URL(
        savedView.customViewLink,
        window.location.origin
      ).toString();
      // Share links always include sharedView for recipients.
      const shareLink = appendSharedViewFlagToUrl(absoluteLink);

      await navigator.clipboard.writeText(shareLink);
      ToastQueue.positive('Link copied to clipboard!', { timeout: 3000 });
    } catch {
      ToastQueue.negative('Failed to copy link to clipboard', {
        timeout: 3000
      });
    }
  }, []);

  // My Stuff is a built-in view: reuse the currently-loaded data (already
  // fetched from /moments/my-stuff) instead of re-fetching whenever it's
  // re-selected — e.g. on every Save Views collapse->expand — as long as it's
  // still the active data source. Switching to a different saved view flips
  // isMyStuffViewActive back to false (see replaceViewStateFromSavedView), so
  // the next My Stuff selection correctly triggers a fresh fetch.
  const isMyStuffViewActive = useSelector(selectMyStuffView);
  const handleOpenMyStuff = useCallback(() => {
    // My Stuff shares the same working preferences as Roadmap — if a real
    // Saved View is currently active, restore those shared preferences
    // first so My Stuff never gets viewed through a Saved View's leftover
    // filters. skipFetch: the setMyStuffView + loadReleaseData below already
    // trigger the one fetch this transition needs.
    const cameFromSavedView = isRealSavedViewActive;
    if (cameFromSavedView) {
      restoreSharedPreferences({ skipFetch: true });
    }

    setActiveSavedViewId(MY_STUFF_VIEW_ID);

    if (isMyStuffViewActive && !cameFromSavedView) {
      return;
    }

    dispatch(homeActions.setMyStuffView(true));
    dispatch(homeActions.loadReleaseData({}));
  }, [
    dispatch,
    setActiveSavedViewId,
    isMyStuffViewActive,
    isRealSavedViewActive,
    restoreSharedPreferences
  ]);

  const handleOpenSavedView = useCallback(
    (savedView: SavedView) => {
      try {
        const targetUrl = new URL(
          savedView.customViewLink,
          window.location.origin
        );
        const targetPathname = targetUrl.pathname;
        const params = parseViewStateFromSearchParams(targetUrl.search);
        const inSharedViewTab = isSharedViewMode();

        // Capture the shared Roadmap/My Stuff preferences before they get
        // overwritten below, so leaving this Saved View later can restore
        // them exactly (no-ops if we're already coming from another Saved View).
        stashSharedPreferencesIfNeeded();

        setOpeningSavedViewId(savedView.id);
        markApplyingSavedView();
        setActiveSavedViewId(savedView.id);

        if (!inSharedViewTab) {
          clearSharedViewSession();
        }

        applySavedViewState(dispatch, targetPathname, params, {
          categoryToProductMap,
          onRoadmapIssueIds: stashPendingRoadmapIssueIds,
          skipPreferencePersistence: inSharedViewTab
        });

        if (inSharedViewTab) {
          const navigationLink = generateViewStateDeepLink(
            params,
            targetPathname,
            { markAsSharedView: true }
          );
          const { pathname, search } = new URL(navigationLink);
          syncSharedViewSessionFromSearch(search);
          markSharedViewUrlStateApplied();
          navigate({ pathname, search }, { replace: true });
        } else {
          // Clean URL after preferences are persisted so refresh restores the saved view.
          navigate({ pathname: targetPathname, search: '' }, { replace: true });
        }

        scheduleClearOpeningSavedView();
      } catch {
        clearOpeningSavedView();
        clearActiveSavedView();
        ToastQueue.negative('Unable to open saved view.', { timeout: 3000 });
      }
    },
    [
      categoryToProductMap,
      clearActiveSavedView,
      clearOpeningSavedView,
      dispatch,
      navigate,
      scheduleClearOpeningSavedView,
      stashSharedPreferencesIfNeeded
    ]
  );

  return {
    handleCopyLink,
    handleOpenSavedView,
    handleOpenMyStuff,
    clearActiveSavedView,
    exitActiveSavedView,
    restoreSharedPreferences,
    isRealSavedViewActive,
    openingSavedViewId,
    activeSavedViewId
  };
}
